import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LockKey } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password set. You can now sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-white rounded-lg grid place-items-center p-1.5 shadow">
            <img src="/zenith360-mark.png" alt="Zenith360 HR" className="max-w-full max-h-full object-contain" />
          </div>
          <div className="font-display font-bold text-xl tracking-tight">Zenith360 HR</div>
        </div>
        <div className="border border-zinc-200 bg-white rounded-xl p-7 shadow-sm">
          {!token ? (
            <div className="text-sm text-red-600" data-testid="reset-no-token">
              This reset link is invalid or missing a token. Please request a new one from the sign-in page.
            </div>
          ) : done ? (
            <div className="text-sm text-emerald-700" data-testid="reset-success">
              Your password has been set. Redirecting to sign in…
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 mb-1">Set password</div>
                <h2 className="font-display text-2xl font-bold tracking-tight">Choose a new password</h2>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw">New password</Label>
                <div className="relative">
                  <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                    className="pl-9 rounded-md" placeholder="At least 8 characters"
                    data-testid="reset-password-input" required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirm password</Label>
                <div className="relative">
                  <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="pl-9 rounded-md" placeholder="Re-enter password"
                    data-testid="reset-confirm-input" required />
                </div>
              </div>
              {error && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-md" data-testid="reset-error">{error}</div>}
              <Button type="submit" disabled={loading}
                className="w-full btn-zenith text-white rounded-md h-11"
                data-testid="reset-submit-btn">
                {loading ? "Saving…" : "Set password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
